import Loader from 'Common/Loader';
import DataGrid, { Column, Pager, Paging, SearchPanel, Summary, TotalItem } from 'devextreme-react/cjs/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import React, { lazy, useCallback, useMemo, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { GetStockReport } from '../services/StockReportAPI';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import ShowReportDetailModal from './ShowReportDetailModal';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Button as DevXButton } from 'devextreme-react/button';

interface StockReportData {
    id: string;
    storeName: string;
    storeCode: string;
    materialType: string;
    stockType: number;
    stockCount: number;
    inwardCount: number;
}

const StockReportList: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [detailModalData, setDetailModalData] = useState<{
        id: string;
        materialType: string;
        stockType: number;
    } | null>(null);
    const [dataGridInstance, setDataGridInstance] = useState<any>(null);

    const handleViewDetails = (data: StockReportData) => {
        setDetailModalData({
            id: data.id,
            materialType: data.materialType,
            stockType: data.stockType,
        });
        setShowDetailsModal(true);
    };

    const dataSource = useMemo(() => {
        return new CustomStore({
            key: 'id',
            load: async (loadOptions) => {
                try {
                    setLoading(true);
                    const search = JSON.parse(JSON.stringify(loadOptions));
                    const parameterList = new URLSearchParams();

                    for (const [key, value] of Object.entries(search)) {
                        parameterList.append(key, JSON.stringify(value));
                    }

                    const response = await GetStockReport(parameterList);
                    // console.log(response)
                    return response;
                } catch (error) {
                    console.error("Data loading error:", error);
                    return { data: [], totalCount: 0 };
                } finally {
                    setLoading(false);
                }
            }
        });
    }, []);

    const renderSerialNumber = useCallback((cellData: any) => {
        const rowIndex = cellData.rowIndex + 1 + cellData.component.pageIndex() * cellData.component.pageSize();
        return <span>{rowIndex}</span>;
    }, []);

    const handleExportExcel = useCallback(() => {
        if (!dataGridInstance) return;

        setLoading(true);
        try {
            const dataSource = dataGridInstance.getDataSource();
            dataSource.load().then((data: any[]) => {
                const columns = [
                    { key: 'storeName', name: 'Store Name' },
                    { key: 'storeCode', name: 'Store Code' },
                    { key: 'materialType', name: 'Material Type' },
                    { key: 'stockType', name: 'Stock Type' },
                    { key: 'stockCount', name: 'Stock Count' },
                    { key: 'inwardCount', name: 'Inward Count' }
                ];

                let excelData = [];

                if (data.length > 0) {
                    excelData.push(columns.reduce((obj, col) => ({
                        ...obj,
                        [col.name]: col.name
                    }), {}));

                    data.forEach(item => {
                        excelData.push({
                            'Store Name': item.storeName || '',
                            'Store Code': item.storeCode || '',
                            'Material Type': item.materialType || '',
                            'Stock Type': item.stockType || '',
                            'Stock Count': item.stockCount || 0,
                            'Inward Count': item.inwardCount || 0
                        });
                    });
                } else {
                    excelData.push(columns.reduce((obj, col) => ({
                        ...obj,
                        [col.name]: col.name
                    }), {}));
                }

                const worksheet = XLSX.utils.json_to_sheet(excelData, { skipHeader: true });

                const wscols = columns.map(col => ({ width: col.name.length + 5 }));
                worksheet['!cols'] = wscols;

                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Report");

                const excelBuffer = XLSX.write(workbook, {
                    bookType: "xlsx",
                    type: "array",
                });

                const blob = new Blob([excelBuffer], {
                    type: "application/octet-stream",
                });

                saveAs(blob, `Stock_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
            });
        } catch (error) {
            console.error("Error exporting to Excel:", error);
        } finally {
            setLoading(false);
        }
    }, [dataGridInstance]);

    return (
        <React.Fragment>
            {loading && <Loader />}

            <div className="page-content">
                <div className="container-fluid">
                    <Card>
                        <Card.Header className='d-flex justify-content-between align-items-center'>
                            <h5 className='m-0'>Stock Report</h5>
                            <DevXButton
                                icon="https://img.favpng.com/18/7/13/scalable-vector-graphics-microsoft-excel-clip-art-portable-network-graphics-png-favpng-yynmZdMbFF2A9c7tcbZa6Ewv5.jpg"
                                hint="Export to Excel"
                                onClick={handleExportExcel}
                            />
                        </Card.Header>
                        <Card.Body>
                            <DataGrid
                                dataSource={dataSource}
                                showRowLines={true}
                                keyExpr="id"
                                showBorders={true}
                                columnAutoWidth={true}
                                onInitialized={(e) => setDataGridInstance(e.component)}
                                onContentReady={(e) => {
                                    const hasData = e.component.totalCount() > 0;
                                    e.component.option("pager.visible", hasData);
                                    e.component.option("paging.visible", hasData);
                                }}
                            >
                                <Pager
                                    showPageSizeSelector={true}
                                    allowedPageSizes={[10, 25, 50, 100]}
                                    showNavigationButtons={true}
                                    displayMode="full"
                                    showInfo={true}
                                />
                                <Summary>
                                    <TotalItem />
                                </Summary>
                                <Paging defaultPageSize={10} />
                                <SearchPanel visible width={240} placeholder="Search..." />
                                <Column
                                    caption="Action"
                                    dataType="string"
                                    cellRender={(cellData) => (
                                        <Button
                                            variant="black"
                                            onClick={() => handleViewDetails(cellData.data)}
                                            title="View Details"
                                        >
                                            <FontAwesomeIcon icon={faEye} />
                                        </Button>
                                    )}
                                    allowFiltering={false}
                                />
                                <Column
                                    caption="Sr.No."
                                    dataType="number"
                                    alignment="center"
                                    cellRender={renderSerialNumber}
                                />
                                <Column allowFiltering={true} dataField="storeName" caption="Store Name" dataType="string" />
                                <Column
                                    allowFiltering={true} dataField="storeCode"
                                    caption="Store Code"
                                    dataType="string"
                                    defaultSortOrder="desc"
                                />
                                <Column allowFiltering={true} dataField="materialType" caption="Material Type" dataType="string" />
                                <Column allowFiltering={true} dataField="stockType" caption="Stock Type" dataType="string" />
                                <Column allowFiltering={true} dataField="stockCount" caption="Stock Count" dataType="number" />
                                <Column allowFiltering={true} dataField="inwardCount" caption="Inward Count" dataType="number" />

                            </DataGrid>
                        </Card.Body>
                    </Card>
                </div>
            </div>

            {showDetailsModal && detailModalData && (
                <ShowReportDetailModal
                    selectedId={detailModalData.id}
                    materialType={detailModalData.materialType}
                    StockType={detailModalData.stockType}
                    handleClose={() => setShowDetailsModal(false)}
                />
            )}
        </React.Fragment>
    );
};

export default StockReportList;